import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box,
    Typography,
    Tooltip,
    Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewListIcon from '@mui/icons-material/ViewList';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommonTable from '../UI/Table/CommonTable';

/**
 * AssessmentTableCommon component for displaying and managing assessments using CommonTable
 */
const AssessmentTableCommon = ({
    tableData = [],
    userData,
    onEdit,
    onDelete,
    onPreview,
    onTakeAssessment,
    onViewSubmissions,
    isLoading = false,
    actionButtons = null,
    getAssessmentStatus,
    isAssessmentSubmitted,
    getDisplayDate,
    getDurationDisplay
}) => {
    const [selectedAssessment, setSelectedAssessment] = useState(null);

    // Helper function to determine columns based on user role
    const getColumns = useMemo(() => {
        const baseColumns = [
            { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
            { key: 'assignment_name', label: 'Assessment Name', minWidth: 250 }
        ];

        // Add role-specific columns
        if (userData.user_role !== "User") {
            baseColumns.push(
                { key: 'program_code', label: 'Assigned To', minWidth: 150 },
                { key: 'assignment_type', label: 'Template Name', minWidth: 150 }
            );
        }

        baseColumns.push(
            { key: 'duration', label: userData.user_role === "User" ? 'Assessment Period' : 'Duration', minWidth: 120 }
        );

        if (userData.user_role === "User") {
            baseColumns.push({ key: 'submission_status', label: 'Submission', minWidth: 120 });
        }

        baseColumns.push(
            { key: 'status', label: 'Status', minWidth: 100 },
            { key: 'actions', label: 'Actions', minWidth: 150 }
        );

        return baseColumns;
    }, [userData.user_role]);

    const handleRowSelect = (assessment) => {
        setSelectedAssessment(selectedAssessment?.id === assessment.id ? null : assessment);
    };

    const confirmDelete = (assessmentId, assessmentName) => {
        if (window.confirm(`Are you sure you want to delete "${assessmentName}"?`)) {
            onDelete(assessmentId);
        }
    };

    // Cell rendering function
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );

            case 'assignment_name':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon sx={{ color: '#0a0068', fontSize: 20 }} />
                        <Typography variant="body2" sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            color: '#333',
                            fontSize: '13px'
                        }}>
                            {value}
                        </Typography>
                        {isAssessmentSubmitted(row) && (
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label="Completed"
                                color="success"
                                size="small"
                                sx={{ ml: 1, fontWeight: 500, fontSize: '0.7rem', fontFamily: 'Poppins' }}
                            />
                        )}
                    </Box>
                );

            case 'program_code':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {value.split(',').map((code, idx) => (
                            <Chip
                                key={idx}
                                label={code.trim()}
                                variant="outlined"
                                size="small"
                                sx={{
                                    fontSize: '0.7rem',
                                    height: 20,
                                    fontFamily: 'Poppins',
                                    alignSelf: 'flex-start'
                                }}
                            />
                        ))}
                    </Box>
                );

            case 'assignment_type':
                return (
                    <Typography variant="body2" sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px'
                    }}>
                        {value}
                    </Typography>
                );

            case 'duration':
                return (
                    <Typography variant="body2" sx={{
                        fontFamily: 'Poppins',
                        color: '#666',
                        fontSize: '12px'
                    }}>
                        {getDurationDisplay(row)}
                    </Typography>
                );

            case 'submission_status':
                return isAssessmentSubmitted(row) ? (
                    <Box>
                        <Typography variant="body2" sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            color: '#2e7d32',
                            fontSize: '12px'
                        }}>
                            Submitted
                        </Typography>
                        <Typography variant="caption" sx={{
                            fontFamily: 'Poppins',
                            color: '#666',
                            fontSize: '11px'
                        }}>
                            {getDisplayDate(row)}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        color: '#ff9800'
                    }}>
                        Not Submitted
                    </Typography>
                );

            case 'status':
                const status = getAssessmentStatus(row);
                return (
                    <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            borderRadius: 2,
                            height: 24
                        }}
                    />
                );

            case 'actions':
                const actionStatus = getAssessmentStatus(row);
                const isSubmitted = isAssessmentSubmitted(row);
                const isAvailable = actionStatus.status === 'available' || isSubmitted;

                return (
                    <Stack direction="row" spacing={0.5}>
                        {userData.user_role === "User" ? (
                            // User actions
                            isAvailable ? (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTakeAssessment(row);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontFamily: 'Poppins',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        px: 2,
                                        py: 0.5,
                                        background: 'linear-gradient(45deg, #0a0068 30%, #1a1078 90%)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #1a1078 30%, #0a0068 90%)'
                                        }
                                    }}
                                >
                                    {isSubmitted ? 'View Results' : 'Take Assessment'}
                                </Button>
                            ) : (
                                <Chip
                                    label={actionStatus.status === 'upcoming' ? 'Coming Soon' : 'Assessment Closed'}
                                    color={actionStatus.color}
                                    size="small"
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontWeight: 500,
                                        fontSize: '0.7rem'
                                    }}
                                />
                            )
                        ) : (
                            // Admin/Manager actions
                            <>
                                <Tooltip title="Edit Assessment">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(row);
                                        }}
                                        sx={{
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 16, color: '#666' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Preview Assessment">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPreview(row);
                                        }}
                                        sx={{
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <VisibilityIcon sx={{ fontSize: 16, color: '#666' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="View Submissions">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewSubmissions(row);
                                        }}
                                        sx={{
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <ViewListIcon sx={{ fontSize: 16, color: '#666' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Assessment">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDelete(row.id, row.assignment_name);
                                        }}
                                        sx={{
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16, color: '#666' }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                );

            default:
                return value || 'N/A';
        }
    };

    return (
        <CommonTable
            columns={getColumns}
            data={tableData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedAssessment}
            actionButtons={actionButtons}
            isLoading={isLoading}
            searchPlaceholder="Search assessments..."
        />
    );
};

export default AssessmentTableCommon;